import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ScrollArea } from "@/components/ui/scroll-area";
const Privacy = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground mb-8">Last updated: November 2025</p>

            <ScrollArea className="h-[700px] rounded-lg border p-6">
              <div className="space-y-8 pr-4">
                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Introduction</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Ace Anchor Pty Ltd ("we," "our," or "us") respects your privacy and is committed to protecting your
                    personal information. This Privacy Policy explains how we collect, use, store, and disclose your
                    information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles
                    (APPs).
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">2. Information We Collect</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We may collect personal information directly from you when you:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Book a service or make an inquiry through our website or social media platforms</li>
                    <li>Communicate with us via email, WhatsApp, or other channels</li>
                    <li>Subscribe to our updates or provide feedback</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    The personal information we collect may include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Full name, contact number, and email address</li>
                    <li>Visa and passport details (for booking coordination only)</li>
                    <li>Flight and arrival information</li>
                    <li>Emergency contact details</li>
                    <li>Payment and transaction information</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    We also collect basic non-identifiable data such as browser type, device, and website usage
                    statistics through cookies and analytics tools.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">3. How We Use Your Information</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Your information is collected and used solely for legitimate business purposes, including to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Process and confirm your bookings</li>
                    <li>Communicate service details and updates</li>
                    <li>Provide assistance during arrival and settlement</li>
                    <li>Improve our services and customer experience</li>
                    <li>Comply with legal obligations and prevent fraud</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed font-semibold">
                    We will not sell, rent, or trade your personal information to any third party.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">4. Data Storage and Security</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We store your information securely on protected servers and use encryption and access controls to
                    prevent unauthorized access.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Your data is only accessed by authorized team members who are trained to handle it confidentially.
                    We take all reasonable steps to destroy or de-identify your data once it is no longer required.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">5. Sharing Your Information</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We may share limited personal information with trusted third-party service providers only where
                    necessary, such as:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Airport pickup and transport vendors</li>
                    <li>Accommodation providers (if requested)</li>
                    <li>Payment processors</li>
                    <li>Communication platforms (e.g., WhatsApp, email)</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    All third parties we work with are required to comply with privacy and data security standards.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Cookies and Analytics</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Our website uses cookies and analytics tools (such as Google Analytics and Meta Pixel) to collect
                    non-identifiable information to improve site functionality and marketing performance. You can
                    disable cookies in your browser settings at any time.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">7. International Data Transfers</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    If you are located outside Australia, you acknowledge that your personal information may be
                    transferred to and stored in Australia for processing. We ensure that all international data
                    transfers comply with relevant privacy protections.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">8. Your Rights</h2>
                  <p className="text-muted-foreground mb-4">You have the right to:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Access and correct your personal information</li>
                    <li>Request deletion of your data when it's no longer required</li>
                    <li>Opt out of marketing communications at any time</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    To exercise these rights, please email{" "}
                    <a href="mailto:privacy@aceanchor.au" className="text-primary hover:underline">
                      privacy@aceanchor.au
                    </a>
                    .
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">9. Changes to This Policy</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We may update this Privacy Policy from time to time to reflect changes in law or business practices.
                    Updates will be published on our website, and continued use of our services indicates acceptance of
                    any revised policy.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">10. Contact Us</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    If you have any questions about this Privacy Policy or how your information is handled, please
                    contact:
                  </p>
                  <div className="space-y-2 text-muted-foreground">
                    <p className="font-semibold">Ace Anchor Pty Ltd</p>
                    <p>
                      📧 Email:{" "}
                      <a href="mailto:support@aceanchor.au" className="text-primary hover:underline">
                        support@aceanchor.au
                      </a>
                    </p>
                    <p>
                      📱 WhatsApp:{" "}
                      <a
                        href="https://wa.me/61403601970"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        +61 0403 601 970
                      </a>
                    </p>
                    <p>🏢 Address: Melbourne, Victoria, Australia</p>
                  </div>
                </section>
              </div>
            </ScrollArea>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
export default Privacy;
